import { useEffect, useState } from 'react'
import { Alert, Box, Button, FormControlLabel, Paper, Stack, Switch, TextField, Typography } from '@mui/material'
import { EMAIL, lerConfigRelatorio, lerEmails, salvarConfigRelatorio, type ConfigRelatorio } from '../api/resumos'

/** Para quem a marca manda o resumo semanal e o relatório mensal, e se vai sozinho depois do automático. */
export function DestinatariosRelatorio({ marcaId, marca, agendaLigada }: { marcaId: string; marca: string; agendaLigada: boolean }) {
  const [c, setC] = useState<ConfigRelatorio | null>(null)
  const [texto, setTexto] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)

  useEffect(() => {
    setC(null)
    lerConfigRelatorio(marcaId)
      .then((x) => {
        setC(x)
        setTexto(x.destinatarios.join(', '))
      })
      .catch((e: Error) => setErro(e.message))
  }, [marcaId])

  if (!c) return erro ? <Alert severity="error">{erro}</Alert> : null
  const lista = lerEmails(texto)
  const invalidos = lista.filter((e) => !EMAIL.test(e))
  const mudou = lista.join(',') !== c.destinatarios.join(',')

  const salvar = async (novo: ConfigRelatorio) => {
    setSalvando(true)
    setErro(null)
    setSalvo(false)
    try {
      await salvarConfigRelatorio(marcaId, novo)
      setC(novo)
      setTexto(novo.destinatarios.join(', '))
      setSalvo(true)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
        E-mails de {marca}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Quem recebe os resumos desta marca. O envio automático sai logo depois que o resumo automático é gerado
        {agendaLigada ? '' : ' (ligue o resumo automático, no alto da página)'}; marca com dados de demonstração nunca envia sozinha.
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            label="Destinatários"
            placeholder="diretoria@empresa.com.br, marketing@empresa.com.br"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value)
              setSalvo(false)
            }}
            error={invalidos.length > 0 || lista.length > 20}
            helperText={invalidos.length ? `E-mail inválido: ${invalidos.join(', ')}` : lista.length > 20 ? 'No máximo 20' : 'Separe por vírgula'}
          />
          <Button
            variant="outlined"
            disabled={!mudou || salvando || invalidos.length > 0 || lista.length > 20}
            onClick={() => salvar({ ...c, destinatarios: lista })}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Salvar e-mails
          </Button>
        </Stack>
        <Box>
          <FormControlLabel
            control={<Switch checked={c.envioSemanal} disabled={salvando || mudou} onChange={(e) => salvar({ ...c, envioSemanal: e.target.checked })} />}
            label="Enviar o resumo semanal toda segunda"
          />
          <FormControlLabel
            control={<Switch checked={c.envioMensal} disabled={salvando || mudou} onChange={(e) => salvar({ ...c, envioMensal: e.target.checked })} />}
            label="Enviar o relatório mensal todo dia 1"
          />
        </Box>
        {(c.envioSemanal || c.envioMensal) && !c.destinatarios.length ? <Alert severity="warning">Envio ligado, mas sem destinatários: nada sai.</Alert> : null}
        {erro ? <Alert severity="error">{erro}</Alert> : null}
        {salvo ? (
          <Typography variant="caption" color="success.main">
            Salvo.
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  )
}
